export type ReceivableSettlementRequest = {
  deviceSecret: string;
  sessionToken: string;
  receivableId: string;
  amount: number;
  paymentMethodCode: string;
  receivingDestinationId: string | null;
  tenderedAmount: number | null;
  localChangeAmount: number | null;
  verificationSource: "CASH_TENDERED" | "STAFF_CONFIRMED";
  verificationNote?: string | null;
  requestKey: string;
  clientContext?: Record<string, unknown>;
};

export type ReceivableBalanceReadback = {
  receivable_id: string;
  receivable_no: string;
  customer_id: string;
  credit_account_id: string;
  source_order_id: string;
  source_branch_id: string;
  receivable_status: "OPEN" | "PARTIALLY_PAID" | "PAID" | "VOIDED" | string;
  currency_code: string;
  original_amount: number;
  allocated_amount: number;
  remaining_amount: number;
  due_date: string | null;
  updated_at: string;
};

export type CustomerCreditSummaryReadback = {
  customer_id: string;
  credit_account_id: string | null;
  currency_code?: string;
  credit_limit?: number | null;
  current_outstanding: number;
  available_credit: number | null;
  open_receivable_count: number;
  open_receivables: Array<Record<string, unknown>>;
};

export type ReceivableSettlementResult = {
  ok: boolean;
  contract: string;
  result_status: "COMMITTED" | "IDEMPOTENT_REPLAY" | string;
  idempotent_replay: boolean;
  payment: Record<string, unknown>;
  allocation: Record<string, unknown>;
  receivable_balance: ReceivableBalanceReadback;
  customer_credit_summary: CustomerCreditSummaryReadback;
};

type GatewayEnvelope<T> = {
  ok: boolean;
  data?: T;
  error?: string;
  reason_code?: string;
};

export class PosReceivableSettlementGatewayClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;

  constructor(options: { baseUrl: string; timeoutMs?: number }) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.timeoutMs = options.timeoutMs ?? 15_000;
  }

  async settle(input: ReceivableSettlementRequest): Promise<{
    ok: boolean;
    data: ReceivableSettlementResult | null;
    error: string | null;
    reasonCode: string | null;
  }> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), this.timeoutMs);
    try {
      const response = await fetch(`${this.baseUrl}/api/pos/runtime/receivable-settlement`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          device_secret: input.deviceSecret,
          session_token: input.sessionToken,
          receivable_id: input.receivableId,
          amount: input.amount,
          execution_mode: "ONLINE",
          payment_method_code: input.paymentMethodCode,
          receiving_destination_id: input.receivingDestinationId,
          tendered_amount: input.tenderedAmount,
          local_change_amount: input.localChangeAmount,
          verification_source: input.verificationSource,
          verification_note: input.verificationNote ?? null,
          request_key: input.requestKey,
          client_context: input.clientContext ?? {},
        }),
        credentials: "omit",
        cache: "no-store",
        signal: controller.signal,
      });

      const payload = (await response.json().catch(() => null)) as GatewayEnvelope<ReceivableSettlementResult> | null;
      if (!response.ok || !payload?.ok || !payload.data) {
        return {
          ok: false,
          data: null,
          error: payload?.error ?? `GATEWAY_HTTP_${response.status}`,
          reasonCode: payload?.reason_code ?? null,
        };
      }
      return { ok: true, data: payload.data, error: null, reasonCode: null };
    } catch (error) {
      return {
        ok: false,
        data: null,
        error: error instanceof DOMException && error.name === "AbortError" ? "GATEWAY_TIMEOUT" : "GATEWAY_UNREACHABLE",
        reasonCode: null,
      };
    } finally {
      window.clearTimeout(timeout);
    }
  }
}
